import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaPlus, FaTimes, FaCloudUploadAlt } from 'react-icons/fa';
import logo from '../assets/logo.png';
import './ClientOnboarding.css';

const PROJECT_TYPES = [
    'Website Development',
    'App Development',
    'Both',
];

const KYC_DOCUMENT_TYPES = [
    'Aadhaar Card',
    'PAN Card',
    'Passport',
    'Driving License',
    'Voter ID',
];

const initialForm = {
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    address: '',
    projectType: '',
    projectName: '',
    password: '',
    confirmPassword: '',
};

const ClientOnboarding = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState(initialForm);
    const [features, setFeatures] = useState(['']);
    const [logo_file, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [showPw, setShowPw] = useState(false);
    const [showConfirmPw, setShowConfirmPw] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [kycDocType, setKycDocType] = useState('');
    const [kycFile, setKycFile] = useState(null);
    const [kycFileName, setKycFileName] = useState('');

    /* ── Handlers ── */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleFeatureChange = (index, value) => {
        setFeatures(prev => prev.map((f, i) => i === index ? value : f));
    };

    const addFeature = () => setFeatures(prev => [...prev, '']);

    const removeFeature = (index) =>
        setFeatures(prev => prev.length === 1 ? [''] : prev.filter((_, i) => i !== index));

    const handleLogoFile = (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setErrors(prev => ({ ...prev, logo: 'Please upload a valid image file.' }));
            return;
        }
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
        setErrors(prev => ({ ...prev, logo: '' }));
    };

    const handleLogoDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        handleLogoFile(file);
    };

    const removeLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
    };

    /* ── Validation ── */
    const validate = () => {
        const e = {};
        if (!form.firstName.trim()) e.firstName = 'First name is required.';
        if (!form.lastName.trim()) e.lastName = 'Last name is required.';
        if (!form.email.trim()) e.email = 'Email is required.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email.';
        if (!form.mobile.trim()) e.mobile = 'Mobile number is required.';
        else if (!/^\+?[\d\s\-()]{7,15}$/.test(form.mobile)) e.mobile = 'Enter a valid mobile number.';
        if (!form.address.trim()) e.address = 'Address is required.';
        if (!form.projectType) e.projectType = 'Please select a project type.';
        if (!form.projectName.trim()) e.projectName = 'Project name is required.';
        if (!form.password) e.password = 'Password is required.';
        else if (form.password.length < 8) e.password = 'Password must be at least 8 characters.';
        if (!form.confirmPassword) e.confirmPassword = 'Please confirm your password.';
        else if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match.';
        if (!kycDocType) e.kycDocType = 'Please select a document type.';
        if (!kycFile) e.kycFile = 'Please upload your KYC document.';
        return e;
    };

    /* ── Submit ── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setSubmitting(true);

        const payload = new FormData();
        payload.append('first_name', form.firstName);
        payload.append('last_name', form.lastName);
        payload.append('email', form.email);
        payload.append('mobile', form.mobile);
        payload.append('address', form.address);
        payload.append('project_type', form.projectType);
        payload.append('project_name', form.projectName);
        payload.append('password', form.password);
        const cleanFeatures = features.filter(f => f.trim());
        cleanFeatures.forEach((f, i) => payload.append(`features[${i}]`, f));
        if (logo_file) payload.append('project_logo', logo_file);
        payload.append('kyc_document_type', kycDocType);
        if (kycFile) payload.append('kyc_document', kycFile);

        try {
            const res = await fetch('https://aayushrealproperties.in/nimaibackend/public/api/client-onboarding', {
                method: 'POST',
                body: payload,
            });
            if (!res.ok) throw new Error('Server error');
            setSubmitted(true);
        } catch {
            setErrors({ submit: 'Something went wrong. Please try again or contact support.' });
        } finally {
            setSubmitting(false);
        }
    };

    /* ── Success screen ── */
    if (submitted) {
        return (
            <div className="onboarding-page onboarding-page--light">
                <div className="onboarding-card onboarding-card--light">
                    <div className="onboarding-brand">
                        <img src={logo} alt="Nimai Solutions" />
                        <span className="brand-label--dark">Nimai Solutions</span>
                    </div>
                    <div className="onboarding-success">
                        <div className="success-icon">🎉</div>
                        <h2 className="success-title--dark">You're all set, {form.firstName}!</h2>
                        <p className="success-sub--dark">Your onboarding details have been submitted. Our team will reach out to you shortly.</p>
                        <button
                            className="continue-login-btn"
                            onClick={() => navigate('/client-login')}
                        >
                            Continue to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /* ── Form ── */
    return (
        <div className="onboarding-page">
            <div className="onboarding-card">
                {/* Brand */}
                <div className="onboarding-brand">
                    <img src={logo} alt="Nimai Solutions" />
                    <span>Nimai Solutions</span>
                </div>

                <h1 className="onboarding-title">Client Onboarding</h1>
                <p className="onboarding-subtitle">Fill in your details to get started. We'll take it from here.</p>

                <form onSubmit={handleSubmit} noValidate>

                    {/* ── Personal Info ── */}
                    <p className="form-section-title">Personal Information</p>

                    <div className="form-row">
                        <div className="form-field">
                            <label>First Name</label>
                            <input
                                name="firstName"
                                placeholder="John"
                                value={form.firstName}
                                onChange={handleChange}
                            />
                            {errors.firstName && <span className="field-error">{errors.firstName}</span>}
                        </div>
                        <div className="form-field">
                            <label>Last Name</label>
                            <input
                                name="lastName"
                                placeholder="Doe"
                                value={form.lastName}
                                onChange={handleChange}
                            />
                            {errors.lastName && <span className="field-error">{errors.lastName}</span>}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-field">
                            <label>Email Address</label>
                            <input
                                name="email"
                                type="email"
                                placeholder="john@example.com"
                                value={form.email}
                                onChange={handleChange}
                            />
                            {errors.email && <span className="field-error">{errors.email}</span>}
                        </div>
                        <div className="form-field">
                            <label>Mobile Number</label>
                            <input
                                name="mobile"
                                type="tel"
                                placeholder="+91 98765 43210"
                                value={form.mobile}
                                onChange={handleChange}
                            />
                            {errors.mobile && <span className="field-error">{errors.mobile}</span>}
                        </div>
                    </div>

                    <div className="form-field">
                        <label>Address</label>
                        <textarea
                            name="address"
                            placeholder="123, Street Name, City, State, Country"
                            rows={3}
                            value={form.address}
                            onChange={handleChange}
                        />
                        {errors.address && <span className="field-error">{errors.address}</span>}
                    </div>

                    {/* ── Project Info ── */}
                    <p className="form-section-title">Project Details</p>

                    <div className="form-row">
                        <div className="form-field">
                            <label>Project Type</label>
                            <select
                                name="projectType"
                                value={form.projectType}
                                onChange={handleChange}
                            >
                                <option value="">Select type...</option>
                                {PROJECT_TYPES.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                            {errors.projectType && <span className="field-error">{errors.projectType}</span>}
                        </div>
                        <div className="form-field">
                            <label>Project Name</label>
                            <input
                                name="projectName"
                                placeholder="My Awesome App"
                                value={form.projectName}
                                onChange={handleChange}
                            />
                            {errors.projectName && <span className="field-error">{errors.projectName}</span>}
                        </div>
                    </div>

                    {/* Project Logo */}
                    <div className="form-field">
                        <label>Project Logo <span className="optional-tag">(Optional)</span></label>
                        <div
                            className={`logo-upload-area ${dragging ? 'dragging' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={handleLogoDrop}
                        >
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleLogoFile(e.target.files[0])}
                            />
                            {!logoPreview ? (
                                <>
                                    <div className="logo-upload-icon"><FaCloudUploadAlt /></div>
                                    <p className="logo-upload-text">
                                        <span>Click to upload</span> or drag & drop<br />
                                        PNG, JPG, SVG up to 5 MB
                                    </p>
                                </>
                            ) : (
                                <div className="logo-preview" onClick={(e) => e.stopPropagation()}>
                                    <img src={logoPreview} alt="Logo preview" />
                                    <button type="button" className="logo-preview-remove" onClick={removeLogo}>
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>
                        {errors.logo && <span className="field-error">{errors.logo}</span>}
                    </div>

                    {/* Features List */}
                    <div className="form-field">
                        <label>Features List</label>
                        <div className="features-list">
                            {features.map((feature, index) => (
                                <div className="feature-item" key={index}>
                                    <input
                                        placeholder={`Feature ${index + 1} — e.g. User Authentication`}
                                        value={feature}
                                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="feature-remove-btn"
                                        onClick={() => removeFeature(index)}
                                        title="Remove"
                                    >
                                        <FaTimes />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button type="button" className="add-feature-btn" onClick={addFeature}>
                            <FaPlus /> Add Feature
                        </button>
                    </div>

                    {/* ── KYC ── */}
                    <p className="form-section-title">KYC Verification</p>

                    <div className="form-row">
                        <div className="form-field">
                            <label>Document Type</label>
                            <select
                                value={kycDocType}
                                onChange={e => { setKycDocType(e.target.value); setErrors(p => ({ ...p, kycDocType: '' })); }}
                            >
                                <option value="">Select document...</option>
                                {KYC_DOCUMENT_TYPES.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                            {errors.kycDocType && <span className="field-error">{errors.kycDocType}</span>}
                        </div>
                        <div className="form-field">
                            <label>Upload Document</label>
                            <label className="kyc-upload-label">
                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    style={{ display: 'none' }}
                                    onChange={e => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setKycFile(file);
                                            setKycFileName(file.name);
                                            setErrors(p => ({ ...p, kycFile: '' }));
                                        }
                                    }}
                                />
                                <span className="kyc-upload-btn">
                                    <FaCloudUploadAlt />
                                    {kycFileName ? kycFileName : 'Choose file (JPG, PNG, PDF)'}
                                </span>
                            </label>
                            {errors.kycFile && <span className="field-error">{errors.kycFile}</span>}
                        </div>
                    </div>

                    {/* ── Account ── */}
                    <p className="form-section-title">Account Setup</p>

                    <div className="form-row">
                        <div className="form-field">
                            <label>Password</label>
                            <div className="password-wrapper">
                                <input
                                    name="password"
                                    type={showPw ? 'text' : 'password'}
                                    placeholder="Min. 8 characters"
                                    value={form.password}
                                    onChange={handleChange}
                                />
                                <button type="button" className="pw-toggle" onClick={() => setShowPw(p => !p)}>
                                    {showPw ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            {errors.password && <span className="field-error">{errors.password}</span>}
                        </div>
                        <div className="form-field">
                            <label>Confirm Password</label>
                            <div className="password-wrapper">
                                <input
                                    name="confirmPassword"
                                    type={showConfirmPw ? 'text' : 'password'}
                                    placeholder="Re-enter password"
                                    value={form.confirmPassword}
                                    onChange={handleChange}
                                />
                                <button type="button" className="pw-toggle" onClick={() => setShowConfirmPw(p => !p)}>
                                    {showConfirmPw ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
                        </div>
                    </div>

                    {errors.submit && (
                        <p className="field-error" style={{ marginTop: 8, textAlign: 'center' }}>{errors.submit}</p>
                    )}

                    <button type="submit" className="submit-btn" disabled={submitting}>
                        {submitting ? 'Submitting…' : 'Submit Onboarding'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ClientOnboarding;
